import React, { useCallback, useMemo } from "react";
import { type GetStaticProps, type NextPage } from "next";
import {
  type Page as SanityPage,
  type Banner,
  type SqlPageSimple,
  getAnnouncementBanner,
  getPage,
  getSqlPages,
} from "lib/sanity";
import { ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { Page } from "components/page";
import { SimpleTextHero } from "components/hero/simple-text-hero";
import { ChevronRightIcon } from "ui/icons";
import { getPageMetaImage } from "utils";
import { SimpleCatalog } from "components/simple-catalog";
import { Chip } from "ui/chips";
import { HeroCTA } from "components/hero";
import { Link } from "ui/link";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  data: SqlPageSimple[];
};

const SqlDictionary: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, data }) => {
  // Get unique categories
  const categories: string[] = useMemo(() => {
    const uniqueCategories = new Set(data?.map((item) => item.categoryName));
    return Array.from(uniqueCategories);
  }, [data]);

  const categoryFilterFunction = useCallback((page: SqlPageSimple, currentCategory: string): boolean => {
    return page.categoryName.toLowerCase().includes(currentCategory.toLowerCase());
  }, []);

  const searchFilterFunction = useCallback((page: SqlPageSimple, search: string): boolean => {
    return (
      page.name.toLowerCase().includes(search.toLowerCase()) ||
      page.categoryName.toLowerCase().includes(search.toLowerCase())
    );
  }, []);

  // Callback used by the SimpleCatalog component to render the items displayed in the catalog
  const renderItemsFunction = useCallback((filteredItems: SqlPageSimple[]) => {
    return (
      <UnorderedList variant="unstyled" w="100%" m="0">
        {filteredItems.map((item) => (
          <ListItem key={item.slug} listStyleType="none" _notLast={{ mb: "tiniest" }}>
            <Link
              variant="unstyled"
              className="group"
              href={`/sql-dictionary/${item.slug}`}
              display="flex"
              alignItems="center"
              w="100%"
              borderRadius="base"
              gap="smaller"
              px="small"
              py="smaller"
              transition="background .15s ease-in-out"
              _hover={{ bg: "brand.backgroundSecondary.light" }}
            >
              <Text
                fontSize={{ base: "sm", sm: "md", lg: "brand.md" }}
                fontWeight="semibold"
                width={{ base: "auto", md: "300px", lg: "360px" }}
              >
                {item.name}
              </Text>
              <Chip variant="blue">{item.categoryName}</Chip>

              <ChevronRightIcon
                boxSize={5}
                ml="auto"
                opacity={0}
                transition="opacity .15s ease-in-out"
                _groupHover={{ opacity: 1 }}
              />
            </Link>
          </ListItem>
        ))}
      </UnorderedList>
    );
  }, []);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <SimpleTextHero chipText={page.eyebrow} title={page.title} description={page.description} actions={<HeroCTA />} />

      <SimpleCatalog
        data={data}
        categories={categories}
        categoriesHeading="Categories"
        searchPlaceholder="Search for a SQL function..."
        renderItems={renderItemsFunction}
        onCategoryFilter={categoryFilterFunction}
        onSearch={searchFilterFunction}
      />
    </Page>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const [page, announcementBanner, data] = await Promise.all([
    getPage("/sql-dictionary"),
    getAnnouncementBanner(),
    getSqlPages(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      data,
    },
    revalidate: 60,
  };
};

export default SqlDictionary;
